var _getCurrentApiHost;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable react/jsx-key */ import { useLocationSearch } from '@rocketlane/rocket-router';
import { useBoolean, useInterval } from 'ahooks';
// eslint-disable-next-line no-restricted-imports
import { Button, Space } from 'antd';
import Animate from 'rc-animate';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AuthenticationServiceProvider } from 'api/account';
import { Form } from 'components/atoms/form';
import { Loading } from 'components/atoms/loading';
import { Input } from 'components/facade/input';
import { toastService } from 'components/molecules/toast';
import { Caption01 } from 'components/styles';
import { getCurrentApiHost } from 'config/environment';
import { ROCKETLANE_PRIVACY } from 'constant';
import { COGNITO_ENABLED_KEY } from 'constant/constant';
import { useTranslate } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
import { useAccountSignUpMutation, useIsDomainExistsMutation, useSignUpThroughGoogle } from 'queries/account';
import { getPasswordFormErrorMessage } from 'utils/error';
import { SubDomainRegex, FullNameRegex, MobileNumberRegex } from 'utils/regex';
import { getCurrentUserTz } from 'utils/timezone';
import { companyNameToDomainName } from './util';
const Terms = styled.div`
  ${Caption01};
  text-align: center;
`;
const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: var(--zindex-popover-high);
  background-color: var(--white-90);
`;
const MessageContainer = styled.h2`
  padding: var(--spacing-08);
`;
const DOMAIN_SUFFIX = ((_getCurrentApiHost = getCurrentApiHost()) === null || _getCurrentApiHost === void 0 ? void 0 : _getCurrentApiHost.includes('nitro')) ? '.nitro.run' : '.rocketlane.com';
const getDomainName = (subDomain)=>{
    return subDomain.toLowerCase() + DOMAIN_SUFFIX;
};
const getFirstAndLastName = (fullName)=>{
    const [firstName, ...rest] = fullName.split(' ');
    return {
        firstName,
        lastName: rest.join(' ')
    };
};
const commonEmailTLDs = [
    'yahoo.com',
    'gmail.com',
    'google.com',
    'hotmail.com',
    'me.com',
    'aol.com',
    'mac.com',
    'live.com',
    'googlemail.com',
    'msn.com',
    'hotmail.co.uk',
    'yahoo.co.uk',
    'facebook.com',
    'gmz.com',
    'mail.com',
    'proton',
    'hey'
];
const LoadingOverlay = ()=>{
    const messages = [
        /*#__PURE__*/ _jsx(FormattedMessage, {
            id: "account_setup.loading.setting_up"
        }),
        /*#__PURE__*/ _jsx(FormattedMessage, {
            id: "account_setup.loading.sealing"
        }),
        /*#__PURE__*/ _jsx(FormattedMessage, {
            id: "account_setup.loading.fuel_level"
        }),
        /*#__PURE__*/ _jsx(FormattedMessage, {
            id: "account_setup.loading.seat_belts"
        }),
        /*#__PURE__*/ _jsx(FormattedMessage, {
            id: "account_setup.loading.engine_on"
        }),
        /*#__PURE__*/ _jsx(FormattedMessage, {
            id: "account_setup.loading.launching"
        })
    ];
    const lastIndex = messages.length - 1;
    const [index, setIndex] = useState(0);
    useInterval(()=>{
        if (index < lastIndex) {
            setIndex(index + 1);
        }
    }, 1500);
    return /*#__PURE__*/ _jsxs(LoadingContainer, {
        children: [
            /*#__PURE__*/ _jsx(Loading, {
                size: "large"
            }),
            /*#__PURE__*/ _jsx(MessageContainer, {
                children: messages[index]
            })
        ]
    });
};
export const AccountSetupForm = ({ token, emailId, name, throughGoogleAuth, domainCheckToken })=>{
    var _searchParams_get;
    var _accountMeta_okta;
    const translate = useTranslate();
    const { data: accountMeta } = useAccountMetaQuery();
    const { mutate: completeSignUp } = useAccountSignUpMutation(accountMeta === null || accountMeta === void 0 ? void 0 : (_accountMeta_okta = accountMeta.okta) === null || _accountMeta_okta === void 0 ? void 0 : _accountMeta_okta.cookiesEnabled);
    const { mutate: completeGoogleSignUp } = useSignUpThroughGoogle();
    const { mutateAsync: isDomainExistsAsync, isLoading } = useIsDomainExistsMutation();
    const [showLoader, { setTrue: showLoading, setFalse: hideLoading }] = useBoolean(false);
    const search = useLocationSearch();
    const searchParams = new URLSearchParams(search);
    const mandatePhoneNumber = searchParams.get('mandatePhoneNumber');
    const agent = (_searchParams_get = searchParams.get('agent')) !== null && _searchParams_get !== void 0 ? _searchParams_get : undefined;
    const [form] = Form.useForm();
    let domainFromEmail = emailId.split('@')[1].split('.')[0];
    domainFromEmail = commonEmailTLDs.find((item)=>item.includes(domainFromEmail.toLowerCase())) ? '' : domainFromEmail;
    const handleOnFinish = (values)=>{
        const { domainName, fullName, ...rest } = values;
        const errorTitle = translate('failure');
        const errorMessage = translate('error');
        const passwordValidationUnified = translate('password.validation.unified');
        showLoading();
        if (throughGoogleAuth) {
            completeGoogleSignUp({
                ...rest,
                ...getFirstAndLastName(fullName),
                domainName: getDomainName(domainName),
                timeZone: getCurrentUserTz(),
                signUpHandlerType: 'GOOGLE_SIGNUP',
                credential: token
            }, {
                onError: (error)=>{
                    hideLoading();
                    const { primary, secondary } = getPasswordFormErrorMessage(error, passwordValidationUnified, errorMessage);
                    toastService({
                        title: errorTitle,
                        message: primary
                    }).error();
                    if (secondary != null) {
                        toastService({
                            title: errorTitle,
                            message: secondary
                        }).error();
                    }
                }
            });
        } else {
            localStorage.setItem(COGNITO_ENABLED_KEY, (accountMeta === null || accountMeta === void 0 ? void 0 : accountMeta.okta.authenticationServiceProvider) === AuthenticationServiceProvider.COGNITO ? 'true' : 'false');
            completeSignUp({
                ...rest,
                ...getFirstAndLastName(fullName),
                domainName: getDomainName(domainName),
                timeZone: getCurrentUserTz(),
                agent
            }, {
                onError: (error)=>{
                    hideLoading();
                    const { primary, secondary } = getPasswordFormErrorMessage(error, passwordValidationUnified, errorMessage);
                    toastService({
                        title: errorTitle,
                        message: primary
                    }).error();
                    if (secondary != null) {
                        toastService({
                            title: errorTitle,
                            message: secondary
                        }).error();
                    }
                }
            });
        }
    };
    const handleOnValuesChange = (changed, _)=>{
        const { companyName } = changed;
        if (companyName) {
            form.setFieldsValue({
                domainName: companyNameToDomainName(companyName)
            });
            form.validateFields([
                'domainName'
            ]);
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            showLoader ? /*#__PURE__*/ _jsx(Animate, {
                transitionName: "fade",
                transitionAppear: true,
                children: /*#__PURE__*/ _jsx(LoadingOverlay, {})
            }) : null,
            /*#__PURE__*/ _jsxs(Form, {
                form: form,
                layout: "vertical",
                onFinish: handleOnFinish,
                onValuesChange: handleOnValuesChange,
                initialValues: {
                    token,
                    emailId
                },
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "token",
                        hidden: true
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "emailId",
                        hidden: true
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "fullName",
                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "account_setup.form.full_name"
                        }),
                        required: true,
                        initialValue: name !== null && name !== void 0 ? name : '',
                        rules: [
                            {
                                validator: (_, value)=>{
                                    if (FullNameRegex.test(value)) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(translate('account_setup.form.full_name.error')));
                                }
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Input, {
                            autoFocus: true,
                            placeholder: "John Doe"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "companyName",
                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "account_setup.form.company_name"
                        }),
                        required: true,
                        initialValue: domainFromEmail,
                        rules: [
                            {
                                required: true,
                                message: translate('routes.account_activate.company_name_is_required')
                            },
                            {
                                whitespace: true,
                                message: translate('routes.account_activate.company_name_cannot_be_empty')
                            },
                            {
                                min: 2,
                                message: translate('routes.account_activate.company_name_must_be_at_least_2_characters')
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Input, {
                            autoComplete: "false",
                            placeholder: "Acme"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "domainName",
                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "account_setup.form.subdomain"
                        }),
                        required: true,
                        hasFeedback: true,
                        tooltip: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "account_setup.form.subdomain.helper"
                        }),
                        initialValue: domainFromEmail,
                        rules: [
                            {
                                required: true,
                                message: translate('routes.account_activate.domain_name_is_required')
                            },
                            {
                                whitespace: true,
                                message: translate('routes.account_activate.domain_name_cannot_be_empty')
                            },
                            {
                                min: 2,
                                message: translate('routes.account_activate.domain_name_must_be_at_least_2_characters')
                            },
                            {
                                validator: async (_, value)=>{
                                    /**
                 * Skipping validation for the cases:
                 * 1. when URL and domain from email are same
                 * 2. AND domainName inputValue is same as email domain
                 */ const skipCheck = window.location.hostname.split('.')[0] === domainFromEmail && value === domainFromEmail;
                                    if (skipCheck) {
                                        return Promise.resolve();
                                    }
                                    if (SubDomainRegex.test(value)) {
                                        const response = await isDomainExistsAsync({
                                            token: domainCheckToken || token,
                                            domainName: getDomainName(value),
                                            throughGoogleAuth
                                        });
                                        if (response.domainExists) {
                                            return Promise.reject(new Error(translate('account_setup.form.subdomain.exists')));
                                        }
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(translate('account_setup.form.subdomain.error'));
                                }
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Input, {
                            autoComplete: "off",
                            suffix: DOMAIN_SUFFIX
                        })
                    }),
                    !throughGoogleAuth && /*#__PURE__*/ _jsx(Form.Item, {
                        name: "newPassword",
                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "account_setup.form.password"
                        }),
                        required: true,
                        rules: [
                            {
                                required: true,
                                message: translate('routes.account_activate.password_is_required')
                            },
                            {
                                whitespace: true,
                                message: translate('routes.account_activate.password_cannot_be_empty')
                            },
                            {
                                min: 11,
                                message: translate('routes.account_activate.password_must_be_at_least_11_characters')
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Input, {
                            autoComplete: "off",
                            type: "password"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "contactNumber",
                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "account_setup.form.phone"
                        }),
                        required: true,
                        rules: [
                            {
                                validator: (_, value)=>{
                                    if (value) {
                                        if (MobileNumberRegex.test(value)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(translate('account_setup.form.phone.error')));
                                    }
                                    return Promise.resolve();
                                }
                            },
                            {
                                required: mandatePhoneNumber === 'true',
                                message: translate('routes.account_activate.phone_number_is_required')
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Input, {
                            placeholder: "+1 123 456 7890"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Space, {
                        direction: "vertical",
                        size: 12,
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                type: "primary",
                                block: true,
                                htmlType: "submit",
                                loading: isLoading,
                                disabled: isLoading,
                                children: translate('account_setup.form.start_my_trial')
                            }),
                            /*#__PURE__*/ _jsx(Terms, {
                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "account_setup.terms",
                                    values: {
                                        a: (chunks)=>/*#__PURE__*/ _jsx("a", {
                                                href: ROCKETLANE_PRIVACY,
                                                target: "_blank",
                                                rel: "noreferrer",
                                                children: chunks
                                            })
                                    }
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
